<i18n>
[
    "reviews__reviewSummary--title",
    "reviews__reviews--label",
    "global__viewAll",
    "reviews__rating--label",
    "reviews__stars--label",
    "reviews__fit--label",
    "reviews__fit--label-text",
    "reviews__small--label",
    "reviews__trueToSize--label",
    "reviews__large--label",
    "product__writeReview--label",
    "product__noReviews--label",
]
</i18n>

<template>
    <div>
        <div class="u-text--center u-spacer--4">
            <h3 class="u-spacer--1">
                {{ $t('reviews__reviewSummary--title') }}
            </h3>
            <span class="c-product-reviews-summary__title-reviews-number o-text--body-copy">
                <template v-if="productHasNoReviews">
                    {{ $t('product__noReviews--label') }}
                </template>
                <template v-else>
                    {{ $tc('reviews__reviews--label', reviewSummary.reviewCount) }}
                </template>
            </span>
            <template v-if="shouldShowWriteReviewLink && !productHasNoReviews">
                <span class="c-product-reviews-summary__pipe"></span>
                <BaseButton
                    v-tab-focus="() => writeReview($router)"
                    variant="link"
                    class="c-product-reviews-summary__title-write-view o-text--body-copy"
                >
                    {{ $t('product__writeReview--label') }}
                </BaseButton>
            </template>
        </div>
        <template v-if="!productHasNoReviews">
            <div class="o-row">
                <div
                    :class="[
                        'o-extra-small--12',
                        'o-medium--6',
                        'o-large--5',
                        'o-large--offset-1',
                        'o-extra-large--4',
                        'o-extra-large--offset-2',
                        'u-spacer--4',
                        'c-product-reviews-summary__bar-left'
                    ]"
                >
                    <div class="u-spacer--2 u-text--center o-text--heading-5">
                        {{ $t('reviews__rating--label') }}
                        <span class="o-text--body-copy">
                            {{ $tc('reviews__stars--label', reviewSummary.overallRatingAverage) }}
                        </span>
                    </div>
                    <BarGraph
                        :ratingAverage="reviewSummary.overallRatingAverage"
                        :barType="BAR_GRAPH_STARS"
                    />
                </div>
                <div
                    :class="[
                        'o-extra-small--12',
                        'o-medium--6',
                        'o-large--5',
                        'o-large--offset-6',
                        'o-extra-large--4',
                        'o-large--offset-6',
                        'u-spacer--6',
                        'c-product-reviews-summary__bar-right',
                    ]"
                >
                    <div class="u-spacer--2 u-text--center o-text--heading-5">
                        {{ $t('reviews__fit--label') }}
                        <span class="o-text--body-copy">
                            {{ $tc('reviews__fit--label-text', reviewSummary.fitRatingCount) }}
                        </span>
                    </div>
                    <BarGraph
                        :ratingAverage="reviewSummary.fitRatingAverage"
                        :barType="BAR_GRAPH_FIT"
                    />
                </div>
            </div>
            <div class="c-product-reviews-summary__card-row o-row">
                <ProductReviewCard
                    v-for="(review, index) in reviews"
                    :key="`product-reviews-summary-card-${review.id}`"
                    :cardIndex="index"
                    :class="reviewCardClasses(index)"
                    :reviewDetail="review"
                    :cardType="PRODUCT_REVIEW_CARD_TOP_REVIEWS"
                />
            </div>
        </template>
        <div class="c-product-reviews-summary__button-container o-row u-text--center">
            <div class="o-extra-small--12">
                <UrbnModalLauncher
                    v-if="!productHasNoReviews"
                    :modalOptions="pdpReviewsViewAllBottomModal"
                    class="c-product-reviews-summary__button"
                    variant="secondary"
                >
                    {{ $t('global__viewAll') }}
                </UrbnModalLauncher>
                <BaseButton
                    v-if="shouldShowWriteReviewLink && productHasNoReviews"
                    variant="secondary"
                    class="c-product-reviews-summary__button"
                    @click="writeReview($router)"
                >
                    {{ $t('product__writeReview--label') }}
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script>
import { get } from 'lodash-es';

import { PRODUCT_REVIEW_CARD_TOP_REVIEWS } from '~routes/product/js/product-constants';
import { BAR_GRAPH_STARS, BAR_GRAPH_FIT } from '~modules/core/js/bar-graph-constants';
import { MODALS } from '~modules/modals/js/modal-constants';

import {
    FETCH_PRODUCT_REVIEWS,
    RESET_PRODUCT_REVIEWS,
    WRITE_REVIEW,
} from '~routes/product/js/product-store.js';

import {
    mapInstanceMutations,
    mapInstanceState,
    mapInstanceActions,
    mapInstanceGetters,
} from '~coreModules/core/js/store-utils';
import { getProductModuleName } from '~coreRoutes/product/js/product-utils';

import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';
import BaseButton from '~coreModules/core/components/ui/buttons/BaseButton.vue';

import ProductReviewCard from '~routes/product/components/ProductReviewCard.vue';
import BarGraph from '~modules/core/components/ui/BarGraph.vue';

export default {
    name: 'ProductReviewsSummary',
    components: {
        ProductReviewCard,
        BarGraph,
        UrbnModalLauncher,
        BaseButton,
    },
    data: () => ({
        BAR_GRAPH_FIT,
        BAR_GRAPH_STARS,
        PRODUCT_REVIEW_CARD_TOP_REVIEWS,
        pdpReviewsViewAllBottomModal: MODALS.pdpReviewsDrawer,
    }),
    computed: {
        ...mapInstanceState(getProductModuleName, {
            reviews(state) {
                return get(state, 'reviews.allReviews', []).slice(0, 3);
            },
            reviewSummary: state => get(state.productDetails, 'reviewSummary', {}),
        }),
        ...mapInstanceGetters(getProductModuleName, [
            'productHasNoReviews',
            'shouldShowWriteReviewLink',
        ]),
    },
    mounted() {
        this.resetProductReviews();
        this.fetchProductReviews();
    },
    methods: {
        ...mapInstanceMutations(getProductModuleName, {
            resetProductReviews: RESET_PRODUCT_REVIEWS,
        }),
        ...mapInstanceActions(getProductModuleName, {
            fetchProductReviews: FETCH_PRODUCT_REVIEWS,
            writeReview: WRITE_REVIEW,
        }),
        reviewCardClasses(index) {
            return {
                'o-extra-small--12': true,
                'o-large--4': true,
                'o-large--offset-4': this.reviews.length === 1,
                'o-large--offset-2': this.reviews.length === 2 && index === 0,
            };
        },
    },
};
</script>

<style lang="scss">
    .c-product-reviews-summary {
        width: 100%;
        height: 100%;

        &__title-reviews-number {
            position: relative;
        }

        &__pipe {
            @include pipe;
        }

        &__title-write-view {
            display: inline-block;
            height: 100%;
        }

        &__bar-left {
            @include breakpoint(small) {
                margin: 0 $nu-spacer-4;
            }

            @include breakpoint(medium) {
                margin: 0;
                padding-right: $nu-spacer-4;
            }
        }

        &__bar-right {
            @include breakpoint(small) {
                margin: 0 $nu-spacer-4;
            }

            @include breakpoint(medium) {
                margin: 0;
                padding-left: $nu-spacer-4;
            }
        }

        &__card-row {
            margin-bottom: $nu-spacer-3pt5;

            @include breakpoint(large) {
                margin-bottom: $nu-spacer-4pt5;
            }
        }

        &__button {
            width: 100%;

            @include breakpoint(small) {
                width: 50%;
            }

            @include breakpoint(large) {
                width: 312px;
            }
        }
    }
</style>
